import React, { useEffect, useState, useMemo } from 'react';
import {
  getNewVsReturningClients,
  getClientRetentionTrend,
  getTopSpendingClients
} from '../../services/analytics.service';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const ChartLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);
const ChartEmptyState = ({ message }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 z-10">
    <p className="text-sm text-center">{message}</p>
  </div>
);


/* ------------------------------------
   Date Label Formatter
------------------------------------ */
const formatBucketLabel = (dateStr, bucket) => {
  if (!dateStr) return '';

  const d = new Date(dateStr);
  if (isNaN(d)) return '';

  if (bucket === 'day') {
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short'
    });
  }

  if (bucket === 'week' || bucket === '15-days') {
    const end = new Date(d);
    end.setDate(d.getDate() + (bucket === 'week' ? 6 : 14));
    return `${d.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  }

  return d.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric'
  });
};


const ClientsInsights = ({ filters }) => {
  const [newVsReturning, setNewVsReturning] = useState([]);
  const [retentionTrend, setRetentionTrend] = useState([]);
  const [retentionBucket, setRetentionBucket] = useState('day');
  const [topSpenders, setTopSpenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasNewVsReturningData = useMemo(() => {
  return newVsReturning.some(d => d.ClientCount > 0);
}, [newVsReturning]);




  /* ------------------------------------
     Fetch Data (Parallel)
  ------------------------------------ */
  useEffect(() => {
  if (!filters?.from || !filters?.to) return;

  const controller = new AbortController();

  const fetchData = async () => {
    try {
      setLoading(true);

      const appliedFilters = {
        from: filters.from,
        to: filters.to
      };

      const [
        newReturningRes,
        retentionRes,
        topClientsRes
      ] = await Promise.all([
        getNewVsReturningClients(appliedFilters, controller.signal),
        getClientRetentionTrend(appliedFilters, controller.signal),
        getTopSpendingClients(appliedFilters, controller.signal)
      ]);

      if (controller.signal.aborted) return;

      setNewVsReturning(newReturningRes || []);
      setRetentionTrend(retentionRes?.data || []);
      setRetentionBucket(retentionRes?.bucket || 'day');
      setTopSpenders(topClientsRes || []);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('ClientsInsights request aborted');
        return;
      }

      console.error('ClientsInsights load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [filters?.from, filters?.to]);

  /* ------------------------------------
     Retention Chart Data
  ------------------------------------ */
  const retentionChartData = useMemo(() => {
    return retentionTrend
      .filter(r => r.BucketStart)
      .map(row => ({
        ...row,
        Label: formatBucketLabel(row.BucketStart, retentionBucket)
      }));
  }, [retentionTrend, retentionBucket]);
  const hasRetentionData = useMemo(() => {
  return retentionChartData.some(
    d => d.TotalVisits || d.ReturningVisits || d.NewVisits
  );
}, [retentionChartData]);

const shouldHideRetentionXAxis = useMemo(() => {
  if (!filters?.from || !filters?.to) return false;

  const from = new Date(filters.from);
  const to = new Date(filters.to);

  const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));

  return diffDays > 15;
}, [filters?.from, filters?.to]);
  return (
    <div className="clients-insights space-y-10">

      {/* ==============================
         New vs Returning Clients
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-4">🧍 New vs Returning Clients</h2>

        <div className="relative" style={{ height: 300 }}>
          {loading && <ChartLoader />}

          {!loading && !hasNewVsReturningData && (
            <ChartEmptyState message="No client activity found for this period" />
          )}

          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={newVsReturning}
                dataKey="ClientCount"
                nameKey="ClientType"
                outerRadius={100}
                label
              >
                {newVsReturning.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ==============================
         Client Retention Trend
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6 relative">
        <h2 className="text-xl font-semibold mb-1">📉 Client Retention Trend</h2>
        {/* <p className="text-sm text-gray-500 mb-4">
          Bucketed by <strong>{retentionBucket}</strong>
        </p> */}

        <div className="relative" style={{ height: 320 }}>
          {loading && <ChartLoader />}

          {!loading && !hasRetentionData && (
            <ChartEmptyState message="No retention trend available for this period" />
          )}

          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={retentionChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
  dataKey="Label"
  tickFormatter={(value) =>
    shouldHideRetentionXAxis ? '' : value
  }
  axisLine={true}
  tickLine={true}
/>
              <YAxis />
              <Tooltip />
              <Legend />
              <Line dataKey="TotalVisits" stroke="#3b82f6" strokeWidth={3} />
              <Line dataKey="ReturningVisits" stroke="#10b981" strokeWidth={3} />
              <Line dataKey="NewVisits" stroke="#f59e0b" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ==============================
         Top Spending Clients
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">💰 Top Spending Clients</h2>

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-2 text-left">Client</th>
          <th className="px-4 py-2 text-left">Phone Number</th>
          <th className="px-4 py-2 text-right">Spend</th>
        </tr>
      </thead>

      <tbody>
        {loading ? (
          <tr>
            <td colSpan={3} className="py-6 text-center text-gray-400">
              Loading top clients…
            </td>
          </tr>
        ) : topSpenders.length === 0 ? (
          <tr>
            <td colSpan={3} className="py-6 text-center text-gray-400">
              No spending data found for this period
            </td>
          </tr>
        ) : (
          topSpenders.map((c, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="px-4 py-2">{c.ClientName}</td>
              <td className="px-4 py-2">{c.PhoneNumber || 'N/A'}</td>
              <td className="px-4 py-2 text-right">{(c.TotalSpend || 0).toLocaleString()}</td>
            </tr>
          ))
        )}
      </tbody>
      </table>
    </div>
    </div>
  );
};

export default ClientsInsights;
